import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { SOCIAL_BENEFITS } from "@/lib/index";

const springGentle = { type: "spring" as const, stiffness: 300, damping: 35 };

export default function BenefitsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section id="vorteile" className="py-28 relative overflow-hidden" ref={ref}>
      {/* Background decoration */}
      <div
        className="absolute inset-0 opacity-5"
        style={{ background: "radial-gradient(ellipse at center, oklch(0.60 0.18 185) 0%, transparent 70%)" }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={springGentle}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span
            className="inline-block text-xs font-semibold uppercase tracking-[0.2em] mb-4 px-3 py-1 rounded-full border"
            style={{ color: "oklch(0.72 0.20 55)", borderColor: "oklch(0.72 0.20 55 / 0.3)", background: "oklch(0.72 0.20 55 / 0.08)" }}
          >
            Warum ein Spezialist?
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-5">
            15 Vorteile für{" "}
            <span style={{ color: "oklch(0.72 0.20 55)" }}>Ihr Unternehmen</span>
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Wenn ein Spezialist die gesamte Kommunikation übernimmt – strategisch, messbar und professionell.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {SOCIAL_BENEFITS.map((benefit, i) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={inView ? { opacity: 1, scale: 1, y: 0 } : {}}
              transition={{ ...springGentle, delay: i * 0.04 }}
              whileHover={{ y: -4, transition: { type: "spring", stiffness: 400, damping: 25 } }}
              className="group relative rounded-2xl bg-card border border-border p-5 hover:border-primary/40 transition-all overflow-hidden cursor-default"
              style={{ boxShadow: "0 4px 20px -4px oklch(0.08 0.025 195 / 0.5)" }}
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
                style={{ background: "linear-gradient(135deg, oklch(0.72 0.20 55 / 0.05) 0%, transparent 60%)" }}
              />
              <div className="relative z-10">
                <div className="text-2xl mb-3">{benefit.icon}</div>
                <h4 className="font-heading font-semibold text-foreground text-sm mb-2 leading-tight">{benefit.title}</h4>
                <p className="text-muted-foreground text-xs leading-relaxed opacity-80">{benefit.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springGentle, delay: 0.7 }}
          className="text-center mt-14"
        >
          <button
            onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
            className="px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-base hover:bg-accent transition-all hover:scale-105 glow-orange inline-flex items-center gap-2"
          >
            Jetzt professionelle Betreuung sichern →
          </button>
        </motion.div>
      </div>
    </section>
  );
}
